/** Cancellation, no-show, deposit and reschedule rules. Pure; the UI renders the returned objects. */
import { HOUR, MINUTE, formatTime, toIso, toMs } from './time';
import { percentOf } from './money';
import { paymentMethodsOf, type Agorot, type Appointment, type FeeRule, type IsoUtc, type LinkMethod, type OnlineMethod, type Service, type Settings } from './types';

const ACTIVE = new Set(['awaiting_deposit', 'pending', 'confirmed']);

export function depositPaid(a: Pick<Appointment, 'depositStatus' | 'depositAgorot'>): Agorot {
  return a.depositStatus === 'paid' ? a.depositAgorot : 0;
}

/** Amount a fee rule asks for, capped at the price. */
export function feeFor(rule: FeeRule, priceAgorot: Agorot, paidDeposit: Agorot): Agorot {
  switch (rule.kind) {
    case 'none': return 0;
    case 'fixed': return Math.min(rule.amountAgorot, priceAgorot);
    case 'percent': return Math.min(percentOf(priceAgorot, rule.percent), priceAgorot);
    case 'deposit': return paidDeposit;
  }
}

export interface CancelConsequence {
  /** free: before the window. fee: inside the window and money is owed/withheld. late: inside the window, no fee. */
  kind: 'free' | 'fee' | 'late';
  allowed: boolean;
  deniedReason?: 'not_active' | 'already_started';
  hoursBefore: number;
  windowHours: number;
  feeAgorot: Agorot;
  depositPaidAgorot: Agorot;
  /** Part of the paid deposit to refund. */
  refundAgorot: Agorot;
  /** Fee beyond the paid deposit, which the owner collects directly. */
  feeDueAgorot: Agorot;
}

export function evaluateCancellation(
  input: { appointment: Appointment; settings: Settings; nowMs: number; by: 'client' | 'owner' },
): CancelConsequence {
  const { appointment: a, settings, nowMs, by } = input;
  const hoursBefore = (toMs(a.start) - nowMs) / HOUR;
  const paid = depositPaid(a);
  const base = { hoursBefore, windowHours: settings.cancelWindowH, depositPaidAgorot: paid };
  const free: CancelConsequence = { ...base, kind: 'free', allowed: true, feeAgorot: 0, refundAgorot: paid, feeDueAgorot: 0 };
  if (!ACTIVE.has(a.status)) return { ...free, allowed: false, deniedReason: 'not_active' };
  if (by === 'owner') return free;
  if (hoursBefore <= 0) return { ...free, allowed: false, deniedReason: 'already_started' };
  if (hoursBefore >= settings.cancelWindowH) return free;
  const fee = feeFor(settings.lateCancelFee, a.priceAgorot, paid);
  if (fee === 0) return { ...free, kind: 'late' };
  const withheld = Math.min(fee, paid);
  return { ...base, kind: 'fee', allowed: true, feeAgorot: fee, refundAgorot: paid - withheld, feeDueAgorot: fee - withheld };
}

export interface NoShowConsequence { feeAgorot: Agorot; retainedDepositAgorot: Agorot; feeDueAgorot: Agorot }
export function evaluateNoShow(a: Appointment, settings: Settings): NoShowConsequence {
  const paid = depositPaid(a);
  const fee = feeFor(settings.noShowFee, a.priceAgorot, paid);
  const retained = Math.min(fee, paid);
  return { feeAgorot: fee, retainedDepositAgorot: retained, feeDueAgorot: fee - retained };
}

export type RescheduleCheck =
  | { allowed: true; hoursBefore: number }
  | { allowed: false; reason: 'not_active' | 'already_started' | 'inside_window'; hoursBefore: number };

/** Clients may move an active appointment until `rescheduleWindowH` before it starts. */
export function evaluateReschedule(input: { appointment: Appointment; settings: Settings; nowMs: number; by: 'client' | 'owner' }): RescheduleCheck {
  const { appointment: a, settings, nowMs, by } = input;
  const hoursBefore = (toMs(a.start) - nowMs) / HOUR;
  if (a.status !== 'pending' && a.status !== 'confirmed') return { allowed: false, reason: 'not_active', hoursBefore };
  if (by === 'owner') return { allowed: true, hoursBefore };
  if (hoursBefore <= 0) return { allowed: false, reason: 'already_started', hoursBefore };
  if (hoursBefore < settings.rescheduleWindowH) return { allowed: false, reason: 'inside_window', hoursBefore };
  return { allowed: true, hoursBefore };
}

export interface DepositRule { required: boolean; amountAgorot: Agorot; holdMinutes: number }

/** Online methods the client can use for a deposit. Bit/PayBox need a link or a phone to be usable. */
export function enabledOnlineMethods(settings: Pick<Settings, 'paymentMethods'>): OnlineMethod[] {
  const m = paymentMethodsOf(settings);
  const usable = (x: LinkMethod): boolean => x.enabled && Boolean((x.link ?? '').trim() || (x.phone ?? '').trim());
  return [...(m.card ? ['card' as const] : []), ...(usable(m.bit) ? ['bit' as const] : []), ...(usable(m.paybox) ? ['paybox' as const] : [])];
}

/** Deposit for a service; ignored when the plan has no deposits. Never exceeds the price. */
export function depositRule(service: Service, settings: Settings, plan: { deposits: boolean }): DepositRule {
  const amount = Math.min(service.depositAgorot ?? 0, service.priceAgorot);
  const required = plan.deposits && amount > 0 && enabledOnlineMethods(settings).length > 0;
  return { required, amountAgorot: required ? amount : 0, holdMinutes: settings.depositHoldMin };
}
export const holdExpiry = (nowMs: number, settings: Settings): IsoUtc => toIso(nowMs + settings.depositHoldMin * MINUTE);
/**
 * Remaining hold for display: under an hour "M:SS" (a ticking countdown), an hour or more "עד HH:MM" in the business time zone
 * ("until" / "حتى" for en / ar), so a 12-hour claim hold never reads "719:56".
 */
export function formatHoldRemaining(expiresAtMs: number, nowMs: number, opts: { tz?: string; locale?: string } = {}): string {
  const left = expiresAtMs - nowMs;
  if (left <= 0) return '0:00';
  if (left < HOUR) { const sec = Math.ceil(left / 1000); return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`; }
  const locale = opts.locale ?? 'he-IL';
  const until = locale.startsWith('en') ? 'until' : locale.startsWith('ar') ? 'حتى' : 'עד';
  return `${until} ${formatTime(expiresAtMs, locale, opts.tz)}`;
}
export const isHoldExpired = (a: Appointment, nowMs: number): boolean =>
  a.status === 'awaiting_deposit' && a.holdExpiresAt !== undefined && toMs(a.holdExpiresAt) <= nowMs;
